import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { Notification } from '@/components/Notification/Notification';
import { Box } from '@/components/Box/Box';
import { Resend, ResendWrapper, TwoFALabel } from '@/bundle/Auth/LoginFlow/TwoFAPage/ui/TwoFAForm/styles';
import { TwoFaCodeInput } from '@/bundle/Auth/LoginFlow/TwoFAPage/ui/TwoFaCodeInput/TwoFaCodeInput';
import { Timer } from '@/bundle/Auth/LoginFlow/TwoFAPage/ui/Timer/Timer';
import { Loader } from '@/components/Loader/Loader';
import { Button } from '@/components/Button/Button';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ResponseErrorType } from '@/types/sharedTypes';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { useVerificationCode } from '@/bundle/shared/hooks/useVerificationCode';

type VerifyOtpCodeFormType = {
  email: string;
  isOtpCodeLoading: boolean;
  isResendOtpCodeLoading: boolean;
  otpCodeError?: ResponseErrorType;
  resendOtpCodeError?: ResponseErrorType;
  onConfirm: (code: string) => void;
  onResend: () => void;
};

export const VerifyOtpCodeForm = forwardRef((props: VerifyOtpCodeFormType, ref) => {
  const { email, isOtpCodeLoading, isResendOtpCodeLoading, otpCodeError, resendOtpCodeError, onConfirm, onResend } =
    props;
  const { t } = useTranslation('login');
  const navigate = useNavigate();

  const {
    codeChanged,
    isDisabledSendCode,
    seconds,
    minutes,
    code,
    startTimer,
    setSeconds,
    setMinutes,
    changeCode,
    isDisabledSubmit,
    onSubmitCode,
  } = useVerificationCode(ref, onResend, onConfirm);

  const apiError = getResponseError(otpCodeError) || getResponseError(resendOtpCodeError);
  const hasInputError = !!getResponseError(otpCodeError) && !codeChanged;

  return (
    <>
      <Typography variant='headline4' fontWeight='semibold' color={COLORS.grey[900]} marginBottom='12px'>
        {t('login:otpCodeHeader')}
      </Typography>
      <Typography color={COLORS.grey[950]}>{t('login:otpCodeHeaderMessage')}</Typography>
      <Typography fontWeight='semibold' color={COLORS.grey[950]} marginBottom='12px'>
        {email}.
      </Typography>
      <Typography color={COLORS.grey[600]} marginBottom='14px'>
        {t('login:2faValidPhoneMessage')}
      </Typography>

      {apiError && (
        <Notification variant='error' marginTop='12px' marginBottom='12px'>
          {apiError}
        </Notification>
      )}

      <form onSubmit={onSubmitCode}>
        <Box position='relative' marginBottom='64px'>
          <TwoFALabel>{t('2faInputLabel')}</TwoFALabel>
          <TwoFaCodeInput code={code} onChange={changeCode} hasError={hasInputError} />
          <ResendWrapper>
            {isDisabledSendCode && (
              <Timer minutes={minutes} setMinutes={setMinutes} seconds={seconds} setSeconds={setSeconds} />
            )}
            {isResendOtpCodeLoading ? (
              <Loader inline backgroundColor='transparent' />
            ) : (
              <Resend type='button' onClick={startTimer} isDisabled={isDisabledSendCode}>
                {t('login:2faResendButtonLabel')}
              </Resend>
            )}
          </ResendWrapper>
        </Box>
        <Box display='flex' flexDirection='column' rowGap='12px'>
          <Button type='submit' disabled={isDisabledSubmit} isLoading={isOtpCodeLoading}>
            {t('login:submitButtonLabel')}
          </Button>
          <Button variant='secondary' onClick={() => navigate(-1)}>
            {t('login:backButtonLabel')}
          </Button>
        </Box>
      </form>
    </>
  );
});
